<template>
  <div>
    <label>
      <slot name="title" />
    </label>
    <div class="mb-4">
      <b-row>
        <b-col cols="6" class="pr-0">
          <datepicker
            :id="idDatePicker"
            :ref="`${idDatePicker}`"
            v-model="selected.date"
            placeholder="Date"
            format="D dd MMM yyyy"
            calendar-class="datepicker datepicker-dropdown dropdown-menu"
            :bootstrap-styling="true"
            :disabled-dates="disabledDateCondition"
            :disabled="disabled"
          />
        </b-col>
        <b-col cols="6" class="pl-3 pr-0">
          <b-form-select id="time" v-model="selected.time" :options="hrs" :disabled="disabled" />
        </b-col>
      </b-row>
      <slot name="required-field-date" />
    </div>
  </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker';
import moment from 'moment';
import { sectionMixin, datetimeMixin } from '@/mixins';

export default {
  name: 'BaseDatePicker',

  components: {
    Datepicker
  },

  mixins: [sectionMixin, datetimeMixin],

  props: {
    typeDate: {
      type: String,
      default: null
    },
    /**
     * Only in edit page > Recieving data and emit back to parent component
     */
    formOldData: {
      type: Object,
      default: null
    },
    /**
     * Only in edit page > Exist data from api for validate form
     */
    formExistData: {
      type: Object,
      default: () => {
        return {
          start_at: '',
          start_enrollment: '',
          last_enrollment: '',
          end_at: ''
        };
      }
    },
    // Use to defined id
    idDatePicker: {
      type: String,
      default: ''
    },

    disabled: {
      type: Boolean
    }
  },

  data() {
    return {
      selected: {
        date: null,
        hr: '08',
        min: '00',
        meridiem: 'am',
        time: '09:00'
      }
    };
  },

  computed: {
    /**
     * Comparing now date-time with form date-time
     *
     * @return {Boolean}
     */
    isPassed() {
      return (formDatetime) =>
        moment(this.today('format')).isAfter(formDatetime[this.typeDate], 'hour');
    },

    /**
     * Checking day that user selected is today.
     *
     * @return {Boolean}
     */
    isSelectToday() {
      const { formOldData, typeDate, formatedDatetime } = this;

      return formOldData[typeDate]
        ? moment(formatedDatetime(formOldData[typeDate], 'YYYY-MM-DD')).isSame(
            formatedDatetime(undefined, 'YYYY-MM-DD'),
            'day'
          )
        : false;
    },

    hrs() {
      // *2 for loop with 30 min
      const nextHour = moment().add(1, 'h').hour() * 2;
      const index = moment().minute() >= 30 ? nextHour + 1 : nextHour;

      if (this.isSelectToday) {
        return this.generateTime(index, 48);
      }

      return this.generateTime(0, 48);
    },

    determineDatetime() {
      return {
        selected:
          this.selected.date &&
          moment(
            `${this.formatedDatetime(this.selected.date, 'MM/DD/YYYY')} ${this.selected.time}:00`,
            'MM/DD/YYYY hh:mm:ss'
          )
            .tz(this.userTimezone)
            .format()
      };
    },

    /**
     * Condition to disabled date-picker
     *
     * document: https://www.npmjs.com/package/vuejs-datepicker
     */
    disabledDateCondition() {
      return (this.pageType === 'create' || this.pageType === 'edit') &&
        (this.typeDate === 'start_enrollment' ||
          this.typeDate === 'last_enrollment' ||
          this.typeDate === 'start_at' ||
          this.typeDate === 'end_at')
        ? {
            to: new Date(moment().startOf('day'))
          }
        : {};
    },

    pageType() {
      return this.$route.name.split('/')[2];
    }
  },

  watch: {
    hrs: {
      handler() {
        if (this.isSelectToday) {
          this.selected.time = this.selected.time < this.hrs[0] ? this.hrs[0] : this.selected.time;
        }
      }
    },

    determineDatetime: {
      handler(determineDatetime) {
        if (determineDatetime) {
          this.$emit('emitDate', determineDatetime.selected);
        }
      }
    },

    /**
     * Use in edit page
     */
    formOldData: {
      immediate: true,
      handler() {
        if (
          this.pageType === 'edit' &&
          this.formOldData.start_enrollment &&
          this.typeDate === 'start_enrollment'
        ) {
          this.convertDateTime(this.formOldData.start_enrollment);
        }

        if (
          this.pageType === 'edit' &&
          this.formOldData.last_enrollment &&
          this.typeDate === 'last_enrollment'
        ) {
          this.convertDateTime(this.formOldData.last_enrollment);
        }

        if (this.pageType === 'edit' && this.formOldData.start_at && this.typeDate === 'start_at') {
          this.convertDateTime(this.formOldData.start_at);
        }

        if (this.pageType === 'edit' && this.formOldData.end_at && this.typeDate === 'end_at') {
          this.convertDateTime(this.formOldData.end_at);
        }
      }
    }
  },

  mounted() {
    const inputDatePickers = this.$refs[this.idDatePicker].$children[0].$el.childNodes;
    inputDatePickers.forEach((input) => {
      if (input.type === 'text') input.removeAttribute('readonly');
    });
  },

  methods: {
    convertDateTime(date) {
      const convertDate = new Date(date);
      const time = this.formatedDatetime(date, 'HH:mm');

      this.selected = {
        date: convertDate,
        time
      };
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .datepicker header .prev {
  margin-top: 12px;
  margin-bottom: 12px;
  width: 28px;
  margin-right: 8px;
  height: 16px;
  content: url('../../../assets/chevron-left-solid.svg') !important;
}

::v-deep .datepicker header .next {
  margin-top: 12px;
  margin-bottom: 12px;
  width: 28px;
  margin-right: 8px;
  height: 16px;
  content: url('../../../assets/chevron-right-solid.svg') !important;
}
</style>

<style>
.vdp-datepicker .form-control:disabled {
  color: #d1d2db;
  background-color: #f4f5fd !important;
  opacity: 0.7;
}
</style>
